import React, { useState, useEffect, useRef, useCallback } from "react";
import variables from "../../../globalVariables";
import AutoCompleteInputField from "../AutoCompleteInputField/AutoCompleteInputField";
import axios from "axios";

const sameOptions = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
    return false;
  }
  return a.every(
    (item, index) =>
      item.value === b[index].value && item.label === b[index].label
  );
};

const locationInputField = (props) => {
  const [locations, setLocations] = useState([]);
  const [options, setOptions] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [uri, setUri] = useState("");
  const typingTimeoutRef = useRef(null);
  const locationsRef = useRef(locations);
  locationsRef.current = locations;
  const changeRef = useRef(props.change);
  changeRef.current = props.change;

  const changeHandler = useCallback((fieldData) => {
    let locationDetails = {};
    if (fieldData.valid) {
      const selectedLocation = locationsRef.current.find(
        (loc) => loc.place_name === fieldData.value
      );

      if (selectedLocation) {
        const [longitude, latitude] = selectedLocation.geometry.coordinates;

        locationDetails = {
          name: selectedLocation.place_name,
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
          boundary: selectedLocation.bbox,
        };
      }
    }

    if (changeRef.current) {
      changeRef.current({ ...fieldData, details: locationDetails });
    }
  }, []);

  useEffect(() => {
    if (isTyping || uri === "") {
      if (uri === "" && !isTyping) setIsFetching(false);
      return;
    }
    let cancelled = false;
    setIsFetching(true);
    axios
      .get("/api/location/" + uri)
      .then((res) => {
        if (cancelled) return;
        const features = res.data?.features || [];
        setLocations((prev) => {
          const prevIds = prev.map((feature) => feature.id).join("|");
          const nextIds = features.map((feature) => feature.id).join("|");
          return prevIds === nextIds ? prev : features;
        });
      })
      .catch(() => {
        if (!cancelled) setLocations([]);
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isTyping, uri]);

  const ajaxCallback = useCallback((value) => {
    if (value.length > 2) {
      setLocations([]);
      setUri(encodeURIComponent(value));
      setIsFetching(true);
    } else {
      setLocations([]);
      setUri("");
      setIsFetching(false);
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    setIsTyping(true);
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 2000);
  }, []);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const result = locations.map((location) => ({
      label: location.place_name,
      value: location.place_name,
    }));
    setOptions((prev) => (sameOptions(prev, result) ? prev : result));
  }, [locations]);

  return (
    <React.Fragment>
      <AutoCompleteInputField
        placeholder={props.placeholder}
        change={changeHandler}
        callback={ajaxCallback}
        value={props.value || ""}
        options={options}
        validate={props.validate}
        required={props.required}
        loading={isTyping || isFetching}
        ajax
      />
      <style jsx>{`
        input,
        textarea,
        select {
          border: none;
          margin: 5px 20px 5px 15px;
          width: 90%;
          outline: none;
        }

        input::placeholder,
        textarea::placeholder,
        select::placeholder {
          color: ${variables.secondaryTextColor};
        }

        textarea {
          min-height: 300px;
          padding-top: 15px;
        }

        label {
          color: ${variables.baseTextColor};
        }
      `}</style>
    </React.Fragment>
  );
};

export default React.memo(locationInputField);
